import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SphereMenu from "../components/SphereMenu";
import MutantBox from "../components/MutantBox";
import OnboardingClassModal from "../components/OnboardingClassModal";
import { AuthContext } from "../context/AuthContext";
import { useProgression } from "../context/ProgressionContext";
import CoreGuardian from "../components/guardians/CoreGuardian";
import GuardianVignette from "../components/guardians/GuardianVignette";
import ProfileBackground from "../components/ProfileBackground";
import { useAppBackground } from "../context/AppBackgroundContext";
import { useI18n } from "../i18n";
import { fetchAgentTip } from "../api";
import {
  MAIN_CORE_DISPLAY_MODE_EVENT,
  getMainCoreDisplayMode
} from "../utils/mainCoreDisplayMode";
import "./Main.css";

export const MAIN_CONSCIOUSNESS_REVERIE_PROBABILITY = 0.25;

export function resolveMainConsciousnessRoute(randomValue = Math.random()) {
  return randomValue < MAIN_CONSCIOUSNESS_REVERIE_PROBABILITY ? '/ensonacion' : '/conciencia';
}

const Main = () => {
  const { user } = useContext(AuthContext);
  const { progressionState, advanceStage } = useProgression();
  const { displayedImage, settings } = useAppBackground() || {};
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const mutantBoxRef = useRef(null);
  const [mainCoreDisplayMode, setMainCoreDisplayMode] = useState(() => getMainCoreDisplayMode());
  const [introMutanteAck, setIntroMutanteAck] = useState(false);

  const [tutorialEndAck, setTutorialEndAck] = useState(() => {
    try {
      const lastTipStr = localStorage.getItem('q_last_tutorial_tip_time');
      if (lastTipStr) {
        const lastTipTime = parseInt(lastTipStr, 10);
        if (!isNaN(lastTipTime)) {
          // If less than 24 hours ago, acknowledge it automatically to hide the vignette
          return (Date.now() - lastTipTime) < (24 * 60 * 60 * 1000);
        }
      }
    } catch(e) { }
    return false;
  });

  const [tipMessage, setTipMessage] = useState("Psique estabilizada. El tutorial ha concluido. Si aún te queda energía antes de la fase REM, gástala libremente.");
  const [introConcienciaAck, setIntroConcienciaAck] = useState(false);
  const [introSubconcienciaAck, setIntroSubconcienciaAck] = useState(false);

  useEffect(() => {
    if (progressionState?.stage === 'COMPLETED' && !tutorialEndAck) {
      const currentLang = document.documentElement.lang || 'es';
      fetchAgentTip(currentLang)
        .then((res) => {
          if (res.data && res.data.tip) {
            setTipMessage(res.data.tip);
          }
        })
        .catch((err) => {
          console.warn("[Main] Error fetching tip:", err);
        });
    }
  }, [progressionState?.stage, tutorialEndAck]);

  useEffect(() => {
    if (location.state?.scrollToMutantBox && mutantBoxRef.current) {
      // Scroll suave hacia la caja, centrándola en la pantalla
      mutantBoxRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Limpiar el estado del historial para que no vuelva a saltar al recargar (F5)
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    const refreshDisplayMode = () => setMainCoreDisplayMode(getMainCoreDisplayMode());
    window.addEventListener(MAIN_CORE_DISPLAY_MODE_EVENT, refreshDisplayMode);
    window.addEventListener('storage', refreshDisplayMode);
    return () => {
      window.removeEventListener(MAIN_CORE_DISPLAY_MODE_EVENT, refreshDisplayMode);
      window.removeEventListener('storage', refreshDisplayMode);
    };
  }, []);

  const [showClassModal, setShowClassModal] = useState(false);

  useEffect(() => {
    if (user && !user.characterClass) {
      setShowClassModal(true);
    } else {
      setShowClassModal(false);
    }
  }, [user]);

  const options = useMemo(() => ([
    {
      id: 'consciousness',
      content: t('common.consciousness'),
      route: '/conciencia',
      resolveRoute: resolveMainConsciousnessRoute,
      fontSizeActive: 0.46
    },
    { id: 'subconsciousness', content: t('common.subconsciousness'), route: '/subconciencia', fontSizeActive: 0.39 },
    { id: 'oracle', content: t('common.oracle'), route: '/qoph', fontSizeActive: 0.48 }
  ]), [t]);

  const mainCore = (
    <div
      className={'main-core-shell mode-' + mainCoreDisplayMode}
      aria-label={t('home.coreAria')}
      data-main-core-mode={mainCoreDisplayMode}
    >
      {mainCoreDisplayMode === 'buttons' ? (
        <nav className="main-core-access-strip" aria-label={t('home.coreAria')}>
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              className={'main-core-access ' + option.id}
              onClick={() => {
                const route = typeof option.resolveRoute === 'function' ? option.resolveRoute(option) : option.route;
                navigate(route);
              }}
            >
              {option.content}
            </button>
          ))}
        </nav>
      ) : (
        <div className="main-core-sphere">
          <SphereMenu options={options} radius={3} lockingEnabled={false} />
        </div>
      )}
    </div>
  );

  const mutantBox = (
    <div
      id="mutant-box-container"
      data-agent-section="mutant-box"
      style={{ width: '100%', maxWidth: 900 }}
      ref={mutantBoxRef}
    >
      <MutantBox userId={user?.id} />
    </div>
  );

  if (progressionState?.stage === 'NUCLEO') {
    return (
      <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--bg-color)' }}>
        <div style={{ transform: 'scale(2.5)', marginTop: '25vh', display: 'flex', justifyContent: 'center' }}>
          <CoreGuardian />
        </div>
        <GuardianVignette onContinue={() => advanceStage('ORACULO')} />
      </div>
    );
  }

  if (progressionState?.stage === 'ORACULO') {
    return (
      <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--bg-color)' }}>
        <div style={{ transform: 'scale(2.5)', marginTop: '25vh', display: 'flex', justifyContent: 'center' }}>
          <CoreGuardian />
        </div>
        <GuardianVignette
          message={t('guardian.stages.oraculoMsg')}
          buttonText={t('guardian.stages.oraculoBtn')}
          onContinue={() => navigate('/qoph')}
        />
      </div>
    );
  }

  if (progressionState?.stage === 'CAJA_MUTANTE') {
    if (!introMutanteAck) {
      return (
        <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--bg-color)' }}>
          <div style={{ transform: 'scale(2.5)', marginTop: '25vh', display: 'flex', justifyContent: 'center' }}>
            <CoreGuardian />
          </div>
          <GuardianVignette
            message={t('guardian.stages.mutantMsg')}
            buttonText={t('guardian.stages.mutantBtn')}
            onContinue={() => setIntroMutanteAck(true)}
          />
        </div>
      );
    }

    return (
      <>
        {showClassModal && <OnboardingClassModal onSuccess={() => setShowClassModal(false)} />}
        {settings?.enabled && displayedImage && <ProfileBackground imageData={displayedImage} interactive={false} />}

        {/* The Narrative: Strategic placement of CoreGuardian */}
        <div style={{ position: 'fixed', top: '10%', left: '50%', transform: 'translateX(-50%)', opacity: 0.1, pointerEvents: 'none', zIndex: 0 }}>
          <CoreGuardian />
        </div>

        <div
          style={{
            position: 'relative',
            zIndex: 2,
            width: '100vw',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingTop: '5vh',
            paddingBottom: 40,
            boxSizing: 'border-box',
            overflowX: 'hidden'
          }}
        >
          {/* Cyberpunk RPG HUD Counter */}
          <div className="cyber-hud-counter" style={{ color: 'var(--cyber-cyan)', letterSpacing: '2px', marginBottom: '20px', fontFamily: 'Orbitron, sans-serif', textShadow: '0 0 10px var(--cyber-cyan)', fontSize: '1.2rem', textAlign: 'center', zIndex: 2 }}>
            EXTRAYENDO VECTORES DE COMPORTAMIENTO...<br/>
            Nodos Sincronizados: {progressionState.stage_interactions || 0}/5
          </div>

          {mutantBox}
        </div>
      </>
    );
  }

  if (progressionState?.stage === 'ENSONACION') {
    return (
      <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--bg-color)' }}>
        <div style={{ transform: 'scale(2.5)', marginTop: '25vh', display: 'flex', justifyContent: 'center' }}>
          <CoreGuardian />
        </div>
        <GuardianVignette
          message={t('guardian.stages.ensonacionMsg')}
          buttonText={t('guardian.stages.ensonacionBtn')}
          onContinue={() => navigate('/ensonacion')}
        />
      </div>
    );
  }

  if (progressionState?.stage === 'TAREAS') {
    return (
      <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--bg-color)' }}>
        <div style={{ transform: 'scale(2.5)', marginTop: '25vh', display: 'flex', justifyContent: 'center' }}>
          <CoreGuardian />
        </div>
        <GuardianVignette
          message={t('guardian.stages.tareasMsg')}
          buttonText={t('guardian.stages.tareasBtn')}
          onContinue={() => navigate('/tareas')}
        />
      </div>
    );
  }

  if (progressionState?.stage === "CONCIENCIA") {
    if (!introConcienciaAck) {
      return (
        <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--bg-color)' }}>
          <div style={{ transform: 'scale(2.5)', marginTop: '25vh', display: 'flex', justifyContent: 'center' }}>
            <CoreGuardian />
          </div>
          <GuardianVignette
            message={t('guardian.stages.concienciaMsg')}
            buttonText={t('guardian.stages.concienciaBtn')}
            onContinue={() => setIntroConcienciaAck(true)}
          />
        </div>
      );
    }
    return (
      <>
        {showClassModal && <OnboardingClassModal onSuccess={() => setShowClassModal(false)} />}
        {settings?.enabled && displayedImage && <ProfileBackground imageData={displayedImage} interactive={false} />}

        <div style={{ position: 'fixed', top: '10%', left: '50%', transform: 'translateX(-50%)', opacity: 0.1, pointerEvents: 'none', zIndex: 0 }}>
          <CoreGuardian />
        </div>

        <div
          style={{
            position: 'relative',
            zIndex: 2,
            width: '100vw',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingTop: 12,
            paddingBottom: 40,
            boxSizing: 'border-box',
            overflowX: 'hidden'
          }}
        >
          <div className="cyber-hud-counter" style={{ color: 'var(--cyber-cyan)', letterSpacing: '2px', marginBottom: '20px', fontFamily: 'Orbitron, sans-serif', textShadow: '0 0 10px var(--cyber-cyan)', fontSize: '1.2rem', textAlign: 'center', zIndex: 2 }}>ESTRUCTURANDO PLANO REFLEXIVO...<br/>Nodos Sincronizados: {progressionState.stage_interactions || 0}/2</div>
          {settings?.invertMainOrder ? <>{mutantBox}{mainCore}</> : <>{mainCore}{mutantBox}</>}
        </div>
      </>
    );
  }

  if (progressionState?.stage === "SUBCONCIENCIA") {
    if (!introSubconcienciaAck) {
      return (
        <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--bg-color)' }}>
          <div style={{ transform: 'scale(2.5)', marginTop: '25vh', display: 'flex', justifyContent: 'center' }}>
            <CoreGuardian />
          </div>
          <GuardianVignette
            message={t('guardian.stages.subconcienciaMsg')}
            buttonText={t('guardian.stages.subconcienciaBtn')}
            onContinue={() => setIntroSubconcienciaAck(true)}
          />
        </div>
      );
    }
    return (
      <>
        {showClassModal && <OnboardingClassModal onSuccess={() => setShowClassModal(false)} />}
        {settings?.enabled && displayedImage && <ProfileBackground imageData={displayedImage} interactive={false} />}

        <div style={{ position: 'fixed', top: '10%', left: '50%', transform: 'translateX(-50%)', opacity: 0.1, pointerEvents: 'none', zIndex: 0 }}>
          <CoreGuardian />
        </div>

        <div
          style={{
            position: 'relative',
            zIndex: 2,
            width: '100vw',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingTop: 12,
            paddingBottom: 40,
            boxSizing: 'border-box',
            overflowX: 'hidden'
          }}
        >
          <div className="cyber-hud-counter" style={{ color: 'var(--cyber-cyan)', letterSpacing: '2px', marginBottom: '20px', fontFamily: 'Orbitron, sans-serif', textShadow: '0 0 10px var(--cyber-cyan)', fontSize: '1.2rem', textAlign: 'center', zIndex: 2 }}>DESCENDIENDO AL SUBCONSCIENTE...<br/>Nodos Sincronizados: {progressionState.stage_interactions || 0}/2</div>
          {settings?.invertMainOrder ? <>{mutantBox}{mainCore}</> : <>{mainCore}{mutantBox}</>}
        </div>
      </>
    );
  }

  if (progressionState && progressionState.stage !== "COMPLETED") {
    return <div />; // Safety fallback for any other tutorial stage to prevent rendering the full dashboard
  }

  return (
    <>
      {showClassModal && <OnboardingClassModal onSuccess={() => setShowClassModal(false)} />}
      {!tutorialEndAck && progressionState?.stage === 'COMPLETED' && (
        <GuardianVignette
          message={tipMessage}
          buttonText={t('guardian.stages.understoodBtn')}
          onContinue={() => {
            try { localStorage.setItem('q_last_tutorial_tip_time', Date.now().toString()); } catch (e) {}
            setTutorialEndAck(true);
          }}
        />
      )}
      {settings?.enabled && displayedImage && <ProfileBackground imageData={displayedImage} interactive={false} />}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          width: '100vw',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: 12,
          paddingBottom: 40,
          boxSizing: 'border-box',
          overflowX: 'hidden'
        }}
      >
        {settings?.invertMainOrder ? <>{mutantBox}{mainCore}</> : <>{mainCore}{mutantBox}</>}
      </div>
    </>
  );
};

export default Main;
